import React from 'react';
import { CrudColumn } from './CrudTable';
import { CrudResource } from './defineResource';
import { TenantParams } from './provider';

const NO_VARS: Record<string, unknown> = {};
/** Stable default so CrudPage can call the hook unconditionally every render. */
const useNoTitleVars = () => NO_VARS;

export interface CrudPresentation<TEntity, TValues> {
  titleKey: string;
  /**
   * Render no table header. For a page whose own heading already says the same thing — the
   * storefront area renders the page name as its h1, and "News" / "News" read as a duplicated
   * header. Carbon's TableContainer draws its header only when given a title, so the row-count
   * description goes too; the pagination bar already states it ("1–20 of 25 items"). `titleKey`
   * is still required: it names the form and delete dialogs.
   */
  hideTitle?: boolean;
  descriptionKey: string;
  createLabelKey: string;
  /** Separate from createLabelKey: the modal heading must not say "Create new…" while editing. */
  updateLabelKey: string;
  deleteHeadingKey: string;
  columns: CrudColumn<TEntity>[];
  /**
   * Formik form exposing { submitForm, resetForm } through its imperative handle. CrudPage
   * remounts it whenever the edited row changes — the instance mounted while the modal is
   * closed is the 'create' instance with `initialValues={null}`, and the row-carrying one is
   * discarded on close — so `initialValues` alone drives the initial state and Formik's own
   * `resetForm` is sufficient. Supplying a custom `resetForm` that resets to the create
   * defaults remains harmless; `submitForm` is what CrudPage calls to submit.
   */
  form: React.ComponentType<any>;
  /** Maps form values to the request body — unit conversions, tag splitting, injected ids. */
  toPayload: (values: TValues, params: TenantParams) => any;
  /**
   * Optional hook supplying interpolation vars for titleKey (e.g. restaurantName).
   * A hook rather than a value so the config file can read portal context itself.
   */
  useTitleVars?: () => Record<string, unknown>;
  emptyState?: React.ReactNode;
}

export interface CrudPageConfig<TEntity, TCreate, TUpdate, TScope extends TenantParams, TValues> {
  resource: CrudResource<TEntity, TCreate, TUpdate, TScope>;
  presentation: Required<Pick<CrudPresentation<TEntity, TValues>, 'useTitleVars'>> & CrudPresentation<TEntity, TValues>;
}

export function definePage<TEntity, TCreate, TUpdate, TScope extends TenantParams, TValues>(
  resource: CrudResource<TEntity, TCreate, TUpdate, TScope>,
  presentation: CrudPresentation<TEntity, TValues>
): CrudPageConfig<TEntity, TCreate, TUpdate, TScope, TValues> {
  return {
    resource,
    presentation: { ...presentation, useTitleVars: presentation.useTitleVars ?? useNoTitleVars },
  };
}
